import { Box, Typography, useTheme } from "@mui/material";
import { useSelector } from "react-redux";
import { useState } from "react";
import moment from "moment";

import ReceiptIcon from "@mui/icons-material/Receipt";
import VisibilityIcon from "@mui/icons-material/Visibility";

import ReportTable, { RRow, RCell } from "components/Tables/ReportTable";
import ListPageHeader from "components/list/ListPageHeader";
import ListFilterBar from "components/list/ListFilterBar";
import ListDateFilterBar from "components/list/ListDateFilterBar";
import ListSearchField from "components/list/ListSearchField";
import SupplierFilterSelect from "components/list/SupplierFilterSelect";
import FixedSizeDialog from "components/fixedSizeDialog";
import PurchaseViewForm from "../forms/purchaseForms/purchaseViewForm";
import { currencyFormatter } from "utilities/currencyFormatter";
import { usePurchaseList, useSupplierList } from "api/hooks/inventory";
import { requester } from "api/pesabackend";
import useListDateFilter from "hooks/useListDateFilter";

export default function Purchases({ setIsSuccess }) {
  const theme = useTheme();
  const textPri = theme.palette.neutral.dark;
  const accent = theme.palette.secondary.main;

  const business_branch_id = useSelector((s) => s.authReducer.business_branch_id);
  const {
    purchaseList, count, isLoadingPurchaseList, isLoadingMore, hasMore, loadMore,
    dateFilter, setDateFilter, customRange, setCustomRange, applyCustomRange,
    setSearch, setSupplierFilter,
  } = usePurchaseList(1, business_branch_id);
  const { supplierList } = useSupplierList(1, business_branch_id);

  const { displayDateFilter, handleFilterChange, handleCustomApply } = useListDateFilter({
    dateFilter, setDateFilter, applyCustomRange, customRange,
  });

  const [openView, setOpenView] = useState(false);
  const [viewData, setViewData] = useState({});

  const handleClickView = async (id) => {
    const res = await requester.get(`/inventory/product_purchase/get_form/${id}`);
    if (res.status === 200) { setViewData(res.data.data); setOpenView(true); }
  };

  const columns = [
    { label: "#", width: 40 },
    { label: "Date Added" },
    { label: "Invoice No." },
    { label: "Invoice Date" },
    { label: "Grand Total", align: "right" },
    { label: "Discount", align: "right" },
    { label: "Supplier" },
    { label: "Actions", align: "center" },
  ];

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: theme.palette.background.alt, p: "1.5rem 2rem", display: "flex", flexDirection: "column", gap: "1.5rem" }}>

      <ListPageHeader
        icon={<ReceiptIcon sx={{ fontSize: "22px" }} />}
        title="Purchase Invoices"
        count={count}
      />

      <ListFilterBar>
        <ListDateFilterBar
          embedded
          dateFilter={displayDateFilter}
          customRange={customRange}
          onFilterChange={handleFilterChange}
          onCustomRangeChange={setCustomRange}
          onCustomApply={handleCustomApply}
        />
        <ListSearchField placeholder="Search invoice number…" onSearch={setSearch} />
        <SupplierFilterSelect suppliers={supplierList} onChange={setSupplierFilter} />
      </ListFilterBar>

      <ReportTable
        columns={columns}
        rows={purchaseList}
        loading={isLoadingPurchaseList}
        hasMore={hasMore}
        onLoadMore={loadMore}
        loadingMore={isLoadingMore}
        emptyMessage="No purchase invoices found"
        renderRow={(row, index) => (
          <RRow key={row.id} index={index}>
            <RCell>{index + 1}</RCell>
            <RCell>{moment(row.date_added).format("LLL")}</RCell>
            <RCell><Typography sx={{ fontSize: "13px", fontWeight: 500, color: textPri }}>{row.invoice_number}</Typography></RCell>
            <RCell>{row.purchase_date}</RCell>
            <RCell align="right"><Typography sx={{ fontSize: "13px", fontWeight: 500, color: textPri }}>{currencyFormatter(parseFloat(row.grand_total))}</Typography></RCell>
            <RCell align="right">{currencyFormatter(parseFloat(row.discount))}</RCell>
            <RCell>{row.related_supplier.name}</RCell>
            <RCell align="center">
              <Box onClick={() => handleClickView(row.id)} sx={{ display: "inline-flex", alignItems: "center", gap: "4px", px: "8px", py: "4px", borderRadius: "6px", border: "1px solid rgba(29,158,117,0.3)", cursor: "pointer", color: accent, fontSize: "12px", fontWeight: 500, "&:hover": { backgroundColor: "rgba(29,158,117,0.08)" } }}>
                <VisibilityIcon sx={{ fontSize: "14px" }} /> View
              </Box>
            </RCell>
          </RRow>
        )}
      />

      <FixedSizeDialog open={openView} handleClose={() => setOpenView(false)} dialogHeading="Purchase Invoice" negativeResponse="Close" maxWidth="lg" scroll="body"
        content={<PurchaseViewForm viewData={viewData} />}
      />
    </Box>
  );
}
